/*
 * ListPage Component with Form Persistence
 *
 * Form persistence is optional and controlled by two props:
 *
 * 1. enablePersist (boolean): Enable/disable form persistence
 *    - When true: Form data is saved to sessionStorage and restored when returning to the page
 *    - When false: No form persistence (default behavior)
 *
 * 2. persistKey (string, optional): Custom key for sessionStorage
 *    - If not provided, defaults to `${modelProps.appName}.${modelProps.model}`
 *    - Use a unique key for each page to avoid conflicts
 *    - Example: "nnc.management.list", "nnc.reports.monthly", etc.
 *
 * Usage examples:
 *
 * // No persistence (default)
 * <ListPage ... />
 *
 * // Enable persistence with default key
 * <ListPage enablePersist={true} ... />
 *
 * // Enable persistence with custom key
 * <ListPage enablePersist={true} persistKey="nnc.management.list" ... />
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  GridActionsCellItem,
  GridSortModel,
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid';

import { DataGrid, PageTitle } from '@/components/atoms';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import { useNavigate } from 'react-router';
import {
  FormContainer,
  useForm,
  UseFormReturn,
  FieldValues,
} from 'react-hook-form-mui';

import { useAuth } from '@contexts/auth';
import { BaseAPI, PaginatedListResponse } from '@/services/api';
import { hasPermissionSmart } from '@/utils';
import { ModelProps } from '@/types';
import { DeletePage } from './deletedialog';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Collapse, Fab, Grid, Typography } from '@mui/material';
import { AxiosError, AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import useFormPersist from 'react-hook-form-persist';
import { z } from 'zod';
import { AddUpdate } from '@/const';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePrepareSubmit } from '@/hooks/usepreparesubmit';
import CardsPage from './cardspage';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface ListPageProps<T extends z.ZodTypeAny, TSearch extends z.ZodTypeAny> {
  schema: T;
  schemaSearch: TSearch;
  api: BaseAPI;
  columns: GridColDef[];
  children?: React.ReactElement[] | React.ReactElement;
  modelProps: ModelProps;
  title: string;
  overrideSortField?: (field: string) => string;
  initialState?: any;
  disableDelete?: boolean;
  disableAdd?: boolean;
  disableEdit?: boolean;
  formcontext?: UseFormReturn<FieldValues, any>;
  // onfetch?: (data: PaginatedListResponse<T>) => void;
  exportFunction?: () => void;
  getRowClassName?: (params: any) => string;
  customPath?: string;
  resetDefaultValues?: Record<string, any>;
  cards?: boolean;
  transformQueryParams?: (params: any) => any;
  checkboxSelection?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  setRowSelectionModel?: React.Dispatch<
    React.SetStateAction<GridRowSelectionModel>
  >;
  effect?: any;
  isRowSelectable?: (params: any) => boolean;
  enablePersist?: boolean;
  persistKey?: string; // Optional custom key for form persistence to avoid conflicts between pages
  apiReturn?: (data: any) => void; // Callback to handle API response data
  onRowsChange?: (rows: any[]) => void;
}

const ListPage = <T extends z.ZodTypeAny, TSearch extends z.ZodTypeAny>({
  api,
  columns,
  children,
  modelProps,
  title,
  overrideSortField,
  initialState,
  disableDelete,
  disableAdd,
  disableEdit,
  getRowClassName,
  customPath,
  cards,
  checkboxSelection,
  rowSelectionModel,
  setRowSelectionModel,
  effect,
  isRowSelectable,
  apiReturn,
  onRowsChange,
  ...props
}: ListPageProps<T, TSearch>) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { permissions } = useAuth();
  const [hasAdd, setHasAdd] = useState(Boolean(false));
  const [hasChange, setHasChange] = useState(Boolean(true));
  const [hasDelete, setHasDelete] = useState(Boolean(false));
  const [showDialog, setShowDialog] = useState(Boolean(false));
  const [deleteId, setDeleteId] = useState(Number(-1));
  const [page, setPage] = useState(0);
  const [ordering, setOrdering] = useState<string[]>([]);
  const defaultFormContext = useForm<z.infer<TSearch>>({
    resolver: zodResolver(props.schemaSearch),
  });
  const formContext = props.formcontext ?? defaultFormContext;

  // Salva os valores padrão iniciais para usar no reset
  const initialDefaultValues = useMemo(() => {
    if (props.formcontext) {
      return props.formcontext.formState.defaultValues || {};
    }
    return {};
  }, [props.formcontext]);
  const prepareSubmit = usePrepareSubmit();
  const [filtersOpen, setFiltersOpen] = useState<boolean>(true);

  const persistKey =
    props.persistKey || `${modelProps.appName}.${modelProps.model}`;

  // Custom storage object to handle Day.js dates properly
  const customStorage: Storage = useMemo(
    () => ({
      getItem: (key: string) => {
        const data = window.sessionStorage.getItem(key);
        if (!data) return null;

        try {
          const parsed = JSON.parse(data);
          // Convert date strings back to Day.js objects for date fields
          const dateFields = [
            'start_date',
            'end_date',
            'date',
            'created_at',
            'updated_at',
          ];

          for (const field of dateFields) {
            if (parsed[field] && typeof parsed[field] === 'string') {
              // Check if it's a valid date string and convert to Day.js
              const date = dayjs(parsed[field]);
              if (date.isValid()) {
                parsed[field] = date;
              }
            }
          }

          return JSON.stringify(parsed);
        } catch {
          return data;
        }
      },
      setItem: (key: string, value: string) => {
        try {
          const parsed = JSON.parse(value);
          // Convert Day.js objects to YYYY-MM-DD strings for storage
          const dateFields = [
            'start_date',
            'end_date',
            'date',
            'created_at',
            'updated_at',
          ];

          for (const field of dateFields) {
            if (parsed[field]) {
              // Handle Day.js objects
              if (
                typeof parsed[field] === 'object' &&
                parsed[field].$isDayjsObject
              ) {
                parsed[field] = parsed[field].format('YYYY-MM-DD');
              }
              // Handle Date objects
              else if (parsed[field] instanceof Date) {
                parsed[field] = dayjs(parsed[field]).format('YYYY-MM-DD');
              }
              // Handle ISO date strings
              else if (
                typeof parsed[field] === 'string' &&
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(parsed[field])
              ) {
                parsed[field] = dayjs(parsed[field]).format('YYYY-MM-DD');
              }
            }
          }

          window.sessionStorage.setItem(key, JSON.stringify(parsed));
        } catch {
          window.sessionStorage.setItem(key, value);
        }
      },
      removeItem: (key: string) => {
        window.sessionStorage.removeItem(key);
      },
      clear: () => {
        window.sessionStorage.clear();
      },
      key: (index: number) => {
        return window.sessionStorage.key(index);
      },
      get length() {
        return window.sessionStorage.length;
      },
    }),
    [],
  );

  useFormPersist(`Persist-${persistKey}`, {
    watch: formContext.watch,
    setValue: formContext.setValue,
    storage: customStorage,
    exclude: props.enablePersist ? [] : Object.keys(formContext.getValues()),
  });

  const { getValues, reset } = formContext;
  const {
    data: response,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery<AxiosResponse<PaginatedListResponse<z.infer<T>>>, AxiosError>({
    queryKey: [
      `${modelProps.appName}.${modelProps.model}`,
      page + 1,
      ordering,
      effect,
      i18n.language,
    ],
    queryFn: async () => {
      const valuesParsed = prepareSubmit(getValues());

      let params = {
        ...valuesParsed,
        page: page + 1,
        ordering: ordering,
      };
      if (props.transformQueryParams) {
        params = props.transformQueryParams(params);
      }
      return await api.list<z.infer<T>>(params);
    },
  });

  const [rowCountState, setRowCountState] = React.useState(
    response?.data.count || 0,
  );

  // Force refetch when form values are restored from persistence
  const [initialLoad, setInitialLoad] = React.useState(true);

  useEffect(() => {
    if (props.enablePersist && initialLoad) {
      // Check if data was restored from sessionStorage
      const savedData = customStorage.getItem(`Persist-${persistKey}`);
      if (savedData && Object.keys(JSON.parse(savedData)).length > 0) {
        // Wait a bit for useFormPersist to restore values, then refetch
        const timer = setTimeout(() => {
          setInitialLoad(false);
          refetch();
        }, 200);
        return () => clearTimeout(timer);
      } else {
        setInitialLoad(false);
      }
    } else if (!props.enablePersist) {
      setInitialLoad(false);
    }
  }, [props.enablePersist, initialLoad, refetch, customStorage, persistKey]);

  const rows = !isLoading ? response?.data.results : [];

  useEffect(() => {
    if (onRowsChange && rows) {
      onRowsChange(rows);
    }
  }, [rows, onRowsChange]);

  const handleUpdate = (id: number) => {
    if (customPath) {
      navigate(`/${customPath}${AddUpdate.replace(':id?', String(id))}`);
    } else {
      navigate(
        `/${modelProps.appName}/${modelProps.model}${AddUpdate.replace(':id?', String(id))}`,
      );
    }
  };

  const handleAdd = () => {
    if (customPath) {
      navigate(`/${customPath}${AddUpdate.replace(':id?', '')}`);
    } else {
      navigate(
        `/${modelProps.appName}/${modelProps.model}${AddUpdate.replace(':id?', '')}`,
      );
    }
  };

  const onSubmit = () => {
    setPage(0);
    refetch();
  };
  const getMActions = (id: number): Array<any> => {
    const actions: Array<any> = new Array<any>();

    if (hasChange && !disableEdit) {
      actions.push(
        <GridActionsCellItem
          key={`edit-${id}`}
          icon={<EditIcon />}
          onClick={() => handleUpdate(Number(id))}
          label="edit"
        />,
      );
    }

    if (!disableDelete && hasDelete) {
      actions.push(
        <GridActionsCellItem
          key={`delete-${id}`}
          icon={<DeleteIcon />}
          label="delete"
          onClick={() => {
            setDeleteId(id);
            setShowDialog(true);
          }}
        />,
      );
    }
    return actions;
  };
  const newColumns: any[] = [...columns];

  if (!disableDelete || !disableEdit) {
    newColumns.push({
      field: 'actions',
      type: 'actions',
      headerName: String(t('Actions')),
      getActions: (params: any) => getMActions(Number(params.id)),
      width: 100,
    });
  }

  const handlePageChange = (newModel: GridPaginationModel) => {
    setPage(newModel.page);
  };

  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      response?.data.count !== undefined
        ? response?.data.count
        : prevRowCountState,
    );
  }, [response?.data.count, setRowCountState]);

  useEffect(() => {
    if (permissions != null) {
      if (
        !hasPermissionSmart(
          permissions,
          `${modelProps.appName}.${modelProps.model}`,
        )
      )
        navigate('/404');
      setHasAdd(
        permissions.includes(`${modelProps.appName}.add_${modelProps.model}`),
      );
      setHasChange(
        permissions.includes(
          `${modelProps.appName}.change_${modelProps.model}`,
        ),
      );
      setHasDelete(
        permissions.includes(
          `${modelProps.appName}.delete_${modelProps.model}`,
        ),
      );
    }
  }, [permissions]);

  // useEffect(() => {
  //   if (!isLoading && !isRefetching && props.onfetch) {
  //     props.onfetch(response?.data);
  //   }
  // }, [isLoading, isRefetching]);

  const closeDialog = () => {
    setShowDialog(false);
  };

  const resetForm = () => {
    // Se tem resetDefaultValues explícito, usa ele
    if (props.resetDefaultValues) {
      reset(props.resetDefaultValues);
    } else if (
      props.formcontext &&
      Object.keys(initialDefaultValues).length > 0
    ) {
      // Se tem formcontext customizado (passado como prop), usa os valores padrão salvos
      reset(initialDefaultValues);
    } else {
      // Caso contrário, reset normal
      reset();
    }
    refetch();
    if (props.enablePersist) {
      sessionStorage.removeItem(`Persist-${persistKey}`);
    }
  };

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      const _ordering: string[] = [];
      sortModel.forEach((value: any) => {
        const sort = value.sort == 'asc' ? '' : '-';
        let name = value.field;
        if (overrideSortField != undefined) {
          name = overrideSortField(name);
        }
        _ordering.push(`${sort}${name}`);
      });
      setOrdering(_ordering);
      refetch();
    },
    [],
  );

  useEffect(() => {
    if (apiReturn && response) {
      apiReturn(response.data?.results || []);
    }
  }, [response]);

  return (
    <>
      <PageTitle title={title} />
      <FormContainer formContext={formContext} onSuccess={onSubmit}>
        <Box
          sx={{
            boxShadow: 1,
            bgcolor: 'white !important',
            borderRadius: filtersOpen ? '16px 16px 0 0' : '16px',
            p: 2,
            width: '100%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: filtersOpen ? 0 : 2,
          }}
          onClick={() => setFiltersOpen((prev) => !prev)}
        >
          <FilterListIcon /> {t('Filter')}
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}></Typography>
          {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        {/* Dropdown content */}
        <Collapse in={filtersOpen}>
          <Grid
            container
            sx={{
              boxShadow: 1,
              bgcolor: 'white !important',
              borderRadius: filtersOpen ? '0 0 16px 16px' : '16px',
              p: 2,
              width: '100%',
              mb: filtersOpen ? 2 : 0,
            }}
            alignItems="center"
            gap={3}
          >
            {children}
            <Grid
              size={{ xs: 12, md: props.exportFunction ? 3 : 3 }}
              direction={{ xs: 'column', md: 'row' }}
              container
            >
              <Button
                sx={{
                  width: { xs: '100%', md: 'auto' },
                  mb: { xs: 2, md: 0 },
                  mr: { xs: 0, md: 2 },
                }}
                type="submit"
                variant="contained"
                disableElevation
                startIcon={<FilterListIcon />}
              >
                {t('Filter')}
              </Button>
              <Button
                sx={{ width: { xs: '100%', md: 'auto' } }}
                onClick={resetForm}
                color="secondary"
                variant="contained"
                disableElevation
                startIcon={<ClearIcon />}
              >
                {t('Clear')}
              </Button>
              {props.exportFunction && (
                <Button
                  sx={{ ml: 2 }}
                  onClick={props.exportFunction}
                  color="secondary"
                  variant="contained"
                  disableElevation
                  startIcon={<FileDownloadIcon />}
                >
                  {t('Export')}
                </Button>
              )}
            </Grid>
          </Grid>
        </Collapse>
      </FormContainer>
      {cards ? (
        <>
          {rows && rows.length > 0 ? (
            <CardsPage data={rows} isLoading={isLoading || isRefetching} />
          ) : !isLoading && !isRefetching ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={8}
              px={4}
              sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
              }}
            >
              <SearchOffIcon
                sx={{
                  fontSize: 64,
                  color: 'text.secondary',
                  mb: 2,
                }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('No results found')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('Try adjusting your search filters')}
              </Typography>
            </Box>
          ) : (
            <CardsPage data={[]} isLoading={isLoading || isRefetching} />
          )}

          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={4}
            mt={4}
          >
            <Button
              variant="gradient"
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
              disabled={!response?.data.previous}
            >
              {t('Back')}
            </Button>
            <Box display={'flex'} flexDirection="column" alignItems="center">
              <Typography>
                {t('Page')} {page + 1}
              </Typography>
              <Typography fontSize={14} color="text.secondary">
                {response?.data.current_page_count} / {response?.data.count}
              </Typography>
            </Box>
            <Button
              variant="gradient"
              onClick={() => setPage((prev) => prev + 1)}
              disabled={!response?.data.next}
            >
              {t('Next')}
            </Button>
          </Box>
        </>
      ) : (
        <DataGrid
          columns={newColumns}
          rows={rows}
          loading={isLoading || isRefetching}
          onPaginationModelChange={handlePageChange}
          currentPage={page}
          rowCount={rowCountState}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          initialState={initialState}
          getRowClassName={getRowClassName}
          sx={{
            '& .MuiDataGrid-cell:hover': {
              color: 'black',
            },
            height: 'calc(100vh - 300px)',
          }}
          checkboxSelection={checkboxSelection || false}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            if (setRowSelectionModel)
              setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          isRowSelectable={isRowSelectable}
        />
      )}
      {showDialog && (
        <DeletePage
          modelProps={modelProps}
          id={deleteId}
          api={api}
          text={title}
          close={closeDialog}
        ></DeletePage>
      )}
      {hasAdd && !disableAdd && (
        <Fab
          color="secondary"
          onClick={handleAdd}
          aria-label="add"
          sx={{ position: 'fixed', right: 20, bottom: 20 }}
        >
          <AddIcon />
        </Fab>
      )}
    </>
  );
};

export { ListPage };
